import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/Card';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';
import { Select } from '../../../components/ui/Select';
import { Badge } from '../../../components/ui/Badge';
import { useLoans, useLoan, useEarlyPayoff, useExecuteEarlyPayoff } from '../../../hooks/useCredit';
import type { LoanStatus, CreditProductType } from '../../../types/credit';

const formatCurrency = (value: number) => {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(value / 100);
};

const formatDate = (date: string) => {
  return new Date(date).toLocaleDateString('pt-BR');
};

const statusLabels: Record<LoanStatus, string> = {
  PENDING_ANALYSIS: 'Análise',
  PENDING_APPROVAL: 'Aprovação',
  APPROVED: 'Aprovado',
  REJECTED: 'Rejeitado',
  PENDING_SIGNATURE: 'Assinatura',
  PENDING_DISBURSEMENT: 'Desembolso',
  ACTIVE: 'Ativo',
  DEFAULTED: 'Inadimplente',
  PAID_OFF: 'Quitado',
  RENEGOTIATED: 'Renegociado',
  CANCELLED: 'Cancelado',
};

const statusColors: Record<LoanStatus, 'default' | 'primary' | 'success' | 'warning' | 'error'> = {
  PENDING_ANALYSIS: 'default',
  PENDING_APPROVAL: 'default',
  APPROVED: 'primary',
  REJECTED: 'error',
  PENDING_SIGNATURE: 'default',
  PENDING_DISBURSEMENT: 'primary',
  ACTIVE: 'success',
  DEFAULTED: 'error',
  PAID_OFF: 'success',
  RENEGOTIATED: 'warning',
  CANCELLED: 'default',
};

const productLabels: Record<CreditProductType, string> = {
  PERSONAL_LOAN: 'Pessoal',
  PAYROLL_LOAN: 'Consignado',
  RECEIVABLES_ADVANCE: 'Antecipação',
  WORKING_CAPITAL: 'Capital de Giro',
};

const statusOptions = [
  { value: '', label: 'Todos os Status' },
  { value: 'ACTIVE', label: 'Ativo' },
  { value: 'DEFAULTED', label: 'Inadimplente' },
  { value: 'PAID_OFF', label: 'Quitado' },
  { value: 'RENEGOTIATED', label: 'Renegociado' },
];

const productOptions = [
  { value: '', label: 'Todos os Produtos' },
  { value: 'PERSONAL_LOAN', label: 'Empréstimo Pessoal' },
  { value: 'PAYROLL_LOAN', label: 'Crédito Consignado' },
  { value: 'RECEIVABLES_ADVANCE', label: 'Antecipação' },
  { value: 'WORKING_CAPITAL', label: 'Capital de Giro' },
];

export function ActiveLoans() {
  const [status, setStatus] = useState<LoanStatus | ''>('');
  const [productType, setProductType] = useState<CreditProductType | ''>('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [selectedLoanId, setSelectedLoanId] = useState<string | null>(null);
  const [showPayoffModal, setShowPayoffModal] = useState(false);

  const { data: loansData, isLoading } = useLoans({
    status: status || undefined,
    productType: productType || undefined,
    page,
    limit: 20,
  });

  const { data: selectedLoan } = useLoan(selectedLoanId || '', true);
  const { data: payoffData } = useEarlyPayoff(selectedLoanId || '');
  const executePayoff = useExecuteEarlyPayoff();

  const handlePayoff = async () => {
    if (!selectedLoanId || !payoffData) return;
    try {
      await executePayoff.mutateAsync({
        loanId: selectedLoanId,
        amount: payoffData.totalAmount,
      });
      setShowPayoffModal(false);
      setSelectedLoanId(null);
    } catch (error) {
      console.error('Erro ao quitar:', error);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold">Empréstimos Ativos</h1>
      </div>

      {/* Filtros */}
      <Card>
        <CardContent className="pt-4">
          <div className="flex flex-wrap gap-4 items-end">
            <div className="flex-1 min-w-[200px]">
              <Input
                placeholder="Buscar por cliente..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <Select
              value={status}
              onChange={(e) => setStatus(e.target.value as LoanStatus | '')}
              options={statusOptions}
            />
            <Select
              value={productType}
              onChange={(e) => setProductType(e.target.value as CreditProductType | '')}
              options={productOptions}
            />
            <Button variant="secondary">Exportar</Button>
          </div>
        </CardContent>
      </Card>

      {/* Lista de Empréstimos */}
      <Card>
        <CardHeader>
          <CardTitle>
            {loansData?.total || 0} empréstimos encontrados
          </CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="text-center py-8">Carregando...</div>
          ) : !loansData?.loans.length ? (
            <div className="text-center py-8 text-gray-500">
              Nenhum empréstimo encontrado
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-3 px-2">Cliente</th>
                    <th className="text-left py-3 px-2">Produto</th>
                    <th className="text-right py-3 px-2">Valor</th>
                    <th className="text-center py-3 px-2">Parcelas</th>
                    <th className="text-right py-3 px-2">Saldo</th>
                    <th className="text-center py-3 px-2">Status</th>
                    <th className="text-center py-3 px-2">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {loansData.loans.map((loan) => (
                    <tr key={loan.id} className="border-b hover:bg-gray-50">
                      <td className="py-3 px-2">
                        <div>
                          <p className="font-medium">{loan.customerName || 'Cliente'}</p>
                          <p className="text-sm text-gray-500">
                            {formatDate(loan.createdAt)}
                          </p>
                        </div>
                      </td>
                      <td className="py-3 px-2">
                        {productLabels[loan.productType]}
                      </td>
                      <td className="text-right py-3 px-2">
                        {formatCurrency(loan.amount)}
                      </td>
                      <td className="text-center py-3 px-2">
                        {loan.paidInstallments || 0}/{loan.installments}
                      </td>
                      <td className="text-right py-3 px-2">
                        {formatCurrency(loan.outstandingBalance || loan.amount)}
                      </td>
                      <td className="text-center py-3 px-2">
                        <Badge variant={statusColors[loan.status]}>
                          {statusLabels[loan.status]}
                        </Badge>
                      </td>
                      <td className="text-center py-3 px-2">
                        <div className="flex gap-1 justify-center">
                          <Button
                            size="sm"
                            variant="secondary"
                            onClick={() => setSelectedLoanId(loan.id)}
                          >
                            Detalhes
                          </Button>
                          {loan.status === 'ACTIVE' && (
                            <Button
                              size="sm"
                              onClick={() => {
                                setSelectedLoanId(loan.id);
                                setShowPayoffModal(true);
                              }}
                            >
                              Quitar
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Paginação */}
          {loansData && loansData.totalPages > 1 && (
            <div className="flex justify-center gap-2 mt-4">
              <Button
                variant="secondary"
                size="sm"
                disabled={page === 1}
                onClick={() => setPage(page - 1)}
              >
                Anterior
              </Button>
              <span className="py-2 px-4">
                Página {page} de {loansData.totalPages}
              </span>
              <Button
                variant="secondary"
                size="sm"
                disabled={page === loansData.totalPages}
                onClick={() => setPage(page + 1)}
              >
                Próxima
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Modal de Detalhes */}
      {selectedLoanId && selectedLoan && !showPayoffModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <CardHeader>
              <div className="flex justify-between items-center">
                <CardTitle>Detalhes do Empréstimo</CardTitle>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setSelectedLoanId(null)}
                >
                  Fechar
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-500">Produto</p>
                  <p className="font-medium">{productLabels[selectedLoan.productType]}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Status</p>
                  <Badge variant={statusColors[selectedLoan.status]}>
                    {statusLabels[selectedLoan.status]}
                  </Badge>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Valor Original</p>
                  <p className="font-medium">{formatCurrency(selectedLoan.amount)}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Saldo Devedor</p>
                  <p className="font-medium">
                    {formatCurrency(selectedLoan.outstandingBalance || selectedLoan.amount)}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Taxa Mensal</p>
                  <p className="font-medium">
                    {(selectedLoan.interestRate * 100).toFixed(2)}%
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">CET</p>
                  <p className="font-medium">{selectedLoan.cet?.toFixed(2)}% a.a.</p>
                </div>
              </div>

              {/* Parcelas */}
              {selectedLoan.installments && (
                <div>
                  <h3 className="font-medium mb-2">Parcelas</h3>
                  <div className="max-h-60 overflow-y-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b">
                          <th className="text-left py-2">#</th>
                          <th className="text-left py-2">Vencimento</th>
                          <th className="text-right py-2">Valor</th>
                          <th className="text-center py-2">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(selectedLoan as any).loanInstallments?.map((inst: any) => (
                          <tr key={inst.id} className="border-b">
                            <td className="py-2">{inst.number}</td>
                            <td className="py-2">{formatDate(inst.dueDate)}</td>
                            <td className="text-right py-2">
                              {formatCurrency(inst.amount)}
                            </td>
                            <td className="text-center py-2">
                              <Badge
                                variant={
                                  inst.status === 'PAID'
                                    ? 'success'
                                    : inst.status === 'OVERDUE'
                                    ? 'error'
                                    : 'default'
                                }
                              >
                                {inst.status === 'PAID'
                                  ? 'Pago'
                                  : inst.status === 'OVERDUE'
                                  ? 'Atrasado'
                                  : 'Pendente'}
                              </Badge>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Modal de Quitação */}
      {showPayoffModal && payoffData && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <Card className="w-full max-w-md">
            <CardHeader>
              <CardTitle>Quitação Antecipada</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="bg-blue-50 p-4 rounded-lg">
                <p className="text-sm text-gray-600">Valor para Quitação</p>
                <p className="text-2xl font-bold text-blue-600">
                  {formatCurrency(payoffData.totalAmount)}
                </p>
              </div>

              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span>Saldo Devedor:</span>
                  <span>{formatCurrency(payoffData.outstandingPrincipal)}</span>
                </div>
                <div className="flex justify-between">
                  <span>Juros até hoje:</span>
                  <span>{formatCurrency(payoffData.accruedInterest)}</span>
                </div>
                {payoffData.discount > 0 && (
                  <div className="flex justify-between text-green-600">
                    <span>Desconto:</span>
                    <span>-{formatCurrency(payoffData.discount)}</span>
                  </div>
                )}
              </div>

              <p className="text-sm text-gray-500">
                Válido até: {formatDate(payoffData.validUntil)}
              </p>

              <div className="flex gap-2">
                <Button
                  variant="secondary"
                  className="flex-1"
                  onClick={() => {
                    setShowPayoffModal(false);
                    setSelectedLoanId(null);
                  }}
                >
                  Cancelar
                </Button>
                <Button
                  className="flex-1"
                  onClick={handlePayoff}
                  disabled={executePayoff.isPending}
                >
                  {executePayoff.isPending ? 'Processando...' : 'Confirmar Quitação'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}

export default ActiveLoans;
